import { IPaymentsAndDeductions } from 'API/paymentAndDeductions';
import { PAYMENT_DEDUCTION_STATUS } from 'Enum/paymentsDeduction';
import { replaceZerosWithHyphen } from 'Utils/replaceZerosWithHyphen';

export enum RequestTypeIds {
  refund = 1,
  deduction = 2,
  payment = 3,
}

export interface IAdvancedSearchState {
  requestState: number | null;
  requestNumber: number | null;
  requestType: {
    payment: boolean;
    deduction: boolean;
    refund: boolean;
  };
  dateRange: {
    start: Date | null;
    end: Date | null;
    isYear: boolean;
  };
  activeSearch: {
    text: boolean;
    advanced: boolean;
  };
  dateMinMaxRange: {
    min: Date;
    max: Date;
  };
  requestNumberList: string[];
}

export function AdvancedSearchReducer(
  state: IAdvancedSearchState,
  action: any
): IAdvancedSearchState {
  switch (action.type) {
    case 'SEARCH_FIELDS_DATA_INITIALIZATION': {
      const dateMinMaxRange = action.payload.value.dateMinMaxRange;
      const requestNumberList = action.payload.value.paymentsDeductions.map(
        (paymentDeduction: IPaymentsAndDeductions) =>
          replaceZerosWithHyphen(paymentDeduction.requestNumber)
      );

      return { ...state, dateMinMaxRange, requestNumberList };
    }
    case 'REQUEST_STATE': {
      const requestState = getRequestStateCodeNumber(
        action.payload.value,
        action.payload.options
      );
      return { ...state, requestState };
    }
    case 'REQUEST_NUMBER': {
      const requestNumber = action.payload.value;
      return { ...state, requestNumber };
    }
    case 'SET_REQUEST_TYPES': {
      const requestType = {
        refund: action.payload.requestTypes.includes(RequestTypeIds.refund),
        deduction: action.payload.requestTypes.includes(
          RequestTypeIds.deduction
        ),
        payment: action.payload.requestTypes.includes(RequestTypeIds.payment),
      };
      return { ...state, requestType };
    }
    case 'REQUEST_DATE_RANGE': {
      const dateRange = {
        start: action.payload.value.start,
        end: action.payload.value.end,
        isYear: action.payload.value.isYear,
      };
      return { ...state, dateRange };
    }
    case 'ACTIVE_SEARCH_TEXT': {
      const activeSearch = {
        text: action.payload.value,
        advanced: state.activeSearch.advanced,
      };
      return { ...state, activeSearch };
    }
    case 'ACTIVE_SEARCH_ADVANCED': {
      const activeSearch = {
        text: false,
        advanced: action.payload.value,
      };
      return { ...state, activeSearch };
    }
    case 'REMOVE_SEARCH_OPTION': {
      return RemoveSearchOption(
        state,
        action.payload.searchValueID,
        action.payload.subValueFieldID
      );
    }
    case 'RESET': {
      return {
        ...state,
        requestState: null,
        requestNumber: null,
        requestType: {
          payment: false,
          deduction: false,
          refund: false,
        },
        dateRange: {
          start: null,
          end: null,
          isYear: false,
        },
        activeSearch: {
          text: false,
          advanced: false,
        },
      };
    }
    default:
      return state;
  }
}

/**
 * Given a string option calculates the code number.
 * @param option string option
 * @param options String options
 * @returns Option code number or Null
 */
function getRequestStateCodeNumber(option: string, options: any) {
  switch (option) {
    case options.opened:
      return PAYMENT_DEDUCTION_STATUS.OPENED_IN_SYSTEM;
    case options.review:
      return PAYMENT_DEDUCTION_STATUS.UNDER_REVIEW;
    case options.done:
      return PAYMENT_DEDUCTION_STATUS.APPROVED;
    default:
      return null;
  }
}

/**
 * Resets a selected search field to null.
 * @param state IAdvancedSearchState
 * @param searchValueIDtoRemove SearchOption ID number
 * @returns IAdvancedSearchState
 */
function RemoveSearchOption(
  state: IAdvancedSearchState,
  searchValueIDtoRemove: number,
  subValueFieldID: number
) {
  switch (searchValueIDtoRemove) {
    case 1: {
      const requestState = null;
      return { ...state, requestState };
    }
    case 2: {
      const requestNumber = null;
      return { ...state, requestNumber };
    }
    case 3: {
      const requestType = { ...state.requestType };
      if (subValueFieldID === RequestTypeIds.refund) requestType.refund = false;
      if (subValueFieldID === RequestTypeIds.deduction)
        requestType.deduction = false;
      if (subValueFieldID === RequestTypeIds.payment)
        requestType.payment = false;
      return { ...state, requestType };
    }
    case 4: {
      const dateRange = { ...state.dateRange };
      if (subValueFieldID === 1) dateRange.start = null;
      // 1 stands for start date.
      else dateRange.end = null;
      return { ...state, dateRange };
    }
    default:
      return state;
  }
}
